/**
 * Drafted copy — FR-I3, T-4.7, **gated on spec §9-4**.
 *
 * The gate is the thing to read first. FR-I3 says the firm's onboarding
 * details go to the platform's AI service and drafts land per block as
 * unpublished content the firm accepts, edits or replaces — *and*, in its last
 * sentence, that without the service the template's placeholder copy is used.
 *
 * **There is no AI service.** Spec §9-4 is an open platform ask, so the second
 * sentence is what runs, every time, for every firm. Which means the one thing
 * this panel must never do is imply otherwise: copy labelled "we drafted this
 * for you" over the template's own placeholder text would be the product lying
 * to a firm about its own website. `meta.ai_available` is what the two cases
 * turn on and they get two different sentences.
 *
 * ## Accepting is two acts, deliberately
 *
 * Accepting a draft marks the row accepted; **putting it on a page is a
 * separate act** — the editor adds the block and the firm presses Save. That
 * is not friction for its own sake: `app/platform/ai.py`'s first invariant is
 * that no generated sentence reaches a visitor without a person choosing it,
 * and a draft that inserted itself would have made the row a publication
 * channel.
 *
 * ## The alternative
 *
 * When a model does answer, the placeholder rides along as the draft's one
 * alternative, so the firm is choosing between two things it can see rather
 * than accepting the only thing on offer. Both go on the screen.
 */

import { Alert, Badge, Button, Card, CardBody } from '@fiveox/ui'
import { Check, Loader2, Sparkles, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { site, type AiDraft, type BlockType } from '../../lib/api/site'
import { useSite } from './useSite'

export function DraftsPanel({
  pageId,
  onUse,
}: {
  pageId?: string
  /** Put this copy on the page being edited. The caller adds the block; this
   *  panel never writes to a page. */
  onUse: (type: BlockType, props: Record<string, unknown>) => void
}) {
  const { can } = useEvaluation()
  const mayDraft = can(SITE, 'can_create')
  const maySettle = can(SITE, 'can_update')
  const { vocabulary } = useSite()

  const [drafts, setDrafts] = useState<AiDraft[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [generated, setGenerated] = useState<boolean | null>(null)

  const load = useCallback(async () => {
    const body = await site.drafts('offered')
    setDrafts(body.data)
  }, [])

  useEffect(() => {
    void load().catch(() => setDrafts([]))
  }, [load])

  const generate = async () => {
    setBusy(true)
    setError(null)
    try {
      const body = await site.generateDrafts([], pageId)
      setDrafts((was) => [...body.data, ...was])
      setGenerated(Boolean(body.meta?.ai_available))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Nothing could be drafted just now.')
    } finally {
      setBusy(false)
    }
  }

  const settle = async (draft: AiDraft, status: 'accepted' | 'rejected') => {
    setError(null)
    try {
      await site.settleDraft(draft.id, status)
      setDrafts((was) => was.filter((d) => d.id !== draft.id))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be saved.')
    }
  }

  if (!mayDraft && drafts.length === 0) return null

  const label = (type: string) =>
    vocabulary?.blocks.find((b) => b.type === type)?.label ?? type

  return (
    <Card>
      <CardBody className="space-y-3">
        <div className="flex flex-wrap items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
            <Sparkles size={16} aria-hidden="true" />
          </span>
          <div className="min-w-[14rem] flex-1">
            <h2 className="text-base font-semibold text-foreground">Something to start from</h2>
            <p className="text-sm text-muted-foreground">
              Wording for each section, ready to edit. Nothing here appears on your site until you
              add it to a page and publish.
            </p>
          </div>
          {mayDraft && (
            <Button variant="secondary" size="sm" disabled={busy} onClick={() => void generate()}>
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Writing…
                </>
              ) : (
                'Draft some wording'
              )}
            </Button>
          )}
        </div>

        {error && <Alert tone="error">{error}</Alert>}

        {/* The two sentences, and which one shows is `meta.ai_available`. */}
        {generated === false && (
          <Alert tone="info">
            This is your template&rsquo;s own starting wording, not something written about your
            firm — so read it as a prompt rather than as a draft. Every word is yours to replace.
          </Alert>
        )}
        {generated === true && (
          <Alert tone="info">
            Drafted from the details you gave us. Read it before you use it: it is a starting
            point, and what your site says about your practice is yours to stand behind.
          </Alert>
        )}

        {drafts.length === 0 ? (
          <p className="rounded-xl border border-dashed border-border p-3 text-sm text-muted-foreground">
            Nothing waiting. Press &ldquo;Draft some wording&rdquo; for a set of sections to start
            from.
          </p>
        ) : (
          <div className="space-y-2">
            {drafts.map((draft) => (
              <div key={draft.id} className="rounded-xl border border-border bg-surface-raised p-3">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground">
                    {label(draft.block_type)}
                  </span>
                  <Badge size="sm" tone={draft.source === 'ai' ? 'info' : 'neutral'}>
                    {draft.source === 'ai' ? 'Drafted for you' : 'From your template'}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground">{preview(draft.props)}</p>

                {draft.alternatives.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-xs text-muted-foreground">
                      Another version
                    </summary>
                    {draft.alternatives.map((alt, i) => (
                      <div key={i} className="mt-1.5 rounded-lg border border-border p-2">
                        <p className="text-sm text-muted-foreground">{preview(alt)}</p>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="mt-1"
                          onClick={() => onUse(draft.block_type, alt)}
                        >
                          Use this one instead
                        </Button>
                      </div>
                    ))}
                  </details>
                )}

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Button
                    size="sm"
                    disabled={!pageId}
                    onClick={() => {
                      onUse(draft.block_type, draft.props)
                      if (maySettle) void settle(draft, 'accepted')
                    }}
                  >
                    <Check size={14} /> Add it to this page
                  </Button>
                  {maySettle && (
                    <Button variant="ghost" size="sm" onClick={() => void settle(draft, 'rejected')}>
                      <X size={14} /> No thanks
                    </Button>
                  )}
                  {!pageId && (
                    <span className="text-xs text-muted-foreground">
                      Open a page to add it.
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  )
}

/**
 * One line of a draft, for the card.
 *
 * Reads the block's own most-visible field rather than stringifying the props —
 * a firm should see the sentence it is about to publish, not a JSON blob.
 */
function preview(props: Record<string, unknown>): string {
  for (const key of ['subheading', 'heading', 'intro', 'body']) {
    const value = props[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
    // Rich text is typed nodes, never a string (ADR-0013 §4).
    if (value && typeof value === 'object' && 'nodes' in value) {
      const nodes = (value as { nodes?: { spans?: { text?: string }[] }[] }).nodes ?? []
      const text = nodes
        .flatMap((node) => (node.spans ?? []).map((span) => span.text ?? ''))
        .join(' ')
        .trim()
      if (text) return text
    }
  }
  return 'A section, ready to fill in.'
}
